import React, { useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import ColegioService from '../services/ColegioService';
import ParticularService from '../services/ParticularService';
import UsuarioService from '../services/UsuarioService';
import { useAuth } from '../contexts/AuthContext';
import './SelectColegio.css';

const getColegioName = (colegio) => colegio?.nombre || colegio?.name || colegio?.descripcion || 'Colegio';
const getParticularName = (particular) => particular?.nombre || particular?.Nombre || particular?.name || 'Particular';

const SelectColegio = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { updateToken, logout, user } = useAuth();

  const colegios = useMemo(() => ColegioService.getColegios() || [], []);
  const particulares = useMemo(() => ParticularService.getParticulares() || [], []);
  const activeColegio = useMemo(() => ColegioService.getActiveColegio(), []);
  const activeParticular = useMemo(() => ParticularService.getActiveParticular(), []);
  const roleName = (user?.roleName || user?.nombreRol || user?.role || '').toLowerCase();
  const isParticularRole = roleName.includes('particular');
  const isAlumnoParticular = roleName.includes('alumnoparticular') || (roleName.includes('alumno') && isParticularRole);
  const defaultRedirectPath = isParticularRole
    ? (isAlumnoParticular ? '/particular/alumno-panel' : '/particular')
    : (roleName.includes('alumno') ? '/alumno/panel' : '/dashboard');
  const redirectPath = location.state?.from?.pathname || defaultRedirectPath;
  const items = isParticularRole ? particulares : colegios;
  const activeItem = isParticularRole ? activeParticular : activeColegio;

  useEffect(() => {
    if (activeItem) {
      navigate(redirectPath, { replace: true });
      return;
    }

    if (!items || items.length === 0) {
      navigate('/dashboard', { replace: true });
      return;
    }

    if (items.length === 1) {
      const colegio = items[0];
      if (isParticularRole) {
        ColegioService.setActiveColegio(null);
        ParticularService.setActiveParticular(colegio);
        const particularId = colegio?.id || colegio?.Id;
        if (particularId) {
          UsuarioService.selectParticular(particularId)
            .then((res) => {
              const token = res.token || res.Token;
              const featureFlags = res.ffColegio || res.FFColegio || [];
              if (token) updateToken(token);
              ColegioService.setFeatureFlags(featureFlags);
              navigate(redirectPath, { replace: true });
            })
            .catch(() => navigate(redirectPath, { replace: true }));
        } else {
          navigate(redirectPath, { replace: true });
        }
        return;
      }

      ParticularService.setActiveParticular(null);
      ColegioService.setActiveColegio(colegio);
      const colegioId = colegio?.id || colegio?.Id;
      if (colegioId) {
        UsuarioService.selectColegio(colegioId)
          .then((res) => {
            const token = res.token || res.Token;
            const featureFlags = res.ffColegio || res.FFColegio || [];
            if (token) {
              updateToken(token);
            }
            ColegioService.setFeatureFlags(featureFlags);
            navigate(redirectPath, { replace: true });
          })
          .catch(() => navigate(redirectPath, { replace: true }));
      } else {
        navigate(redirectPath, { replace: true });
      }
    }
  }, [activeItem, items, isParticularRole, navigate, redirectPath, updateToken]);

  const handleSelect = (colegio) => {
    if (isParticularRole) {
      const particularId = colegio?.id || colegio?.Id;
      ColegioService.setActiveColegio(null);
      ParticularService.setActiveParticular(colegio);
      if (!particularId) {
        navigate(redirectPath, { replace: true });
        return;
      }

      UsuarioService.selectParticular(particularId)
        .then((res) => {
          const token = res.token || res.Token;
          const featureFlags = res.ffColegio || res.FFColegio || [];
          if (token) {
            updateToken(token);
          }
          ColegioService.setFeatureFlags(featureFlags);
          navigate(redirectPath, { replace: true });
        })
        .catch(() => {
          navigate(redirectPath, { replace: true });
        });
      return;
    }

    const colegioId = colegio?.id || colegio?.Id;
    ParticularService.setActiveParticular(null);
    ColegioService.setActiveColegio(colegio);
    if (!colegioId) {
      navigate(redirectPath, { replace: true });
      return;
    }

    UsuarioService.selectColegio(colegioId)
      .then((res) => {
        const token = res.token || res.Token;
        const featureFlags = res.ffColegio || res.FFColegio || [];
        if (token) {
          updateToken(token);
        }
        ColegioService.setFeatureFlags(featureFlags);
        navigate(redirectPath, { replace: true });
      })
      .catch(() => {
        navigate(redirectPath, { replace: true });
      });
  };

  if (!items || items.length <= 1 || activeItem) {
    return null;
  }

  return (
    <div className="select-colegio-page">
      <div className="select-colegio-hero">
        <div className="hero-header">
          <div className="hero-content">
            <p className="eyebrow">Configurar entorno</p>
            <h1>{isParticularRole ? 'Elegí tu particular' : 'Elegí tu colegio'}</h1>
            <p className="subtitle">
              {isParticularRole
                ? 'Selecciona la cuenta particular con la que querés trabajar.'
                : 'Selecciona el colegio con el que querés trabajar. Podrás cambiarlo más adelante desde tu perfil.'}
            </p>
          </div>
          <button
            type="button"
            className="select-colegio-logout"
            onClick={logout}
            title="Cerrar sesión"
          >
            <i className="fas fa-sign-out-alt" />
            <span>Salir</span>
          </button>
        </div>
      </div>

      <div className="select-colegio-card">
        <div className="card-header">
          <div>
            <p className="card-kicker">{isParticularRole ? 'Particulares disponibles' : 'Colegios disponibles'}</p>
            <h2>Selecciona una opción</h2>
            <p className="card-subtitle">
              Mostrando {items.length} {isParticularRole ? 'particulares' : 'colegios'} asociados a tu cuenta.
            </p>
          </div>
        </div>

        <div className="colegio-grid">
          {items.map((colegio) => (
            <button
              key={colegio.id || colegio.Id || (isParticularRole ? getParticularName(colegio) : getColegioName(colegio))}
              className="colegio-card"
              type="button"
              onClick={() => handleSelect(colegio)}
            >
              <div className="colegio-icon">
                <i className={isParticularRole ? 'fas fa-user-tie' : 'fas fa-map-marker-alt'} />
              </div>
              <div className="colegio-info">
                <h3>{isParticularRole ? getParticularName(colegio) : getColegioName(colegio)}</h3>
                {colegio.descripcion && <p className="colegio-desc">{colegio.descripcion}</p>}
                {colegio.detalle && <p className="colegio-desc">{colegio.detalle}</p>}
              </div>
              <div className="colegio-action">
                <span>Elegir</span>
                <i className="fas fa-arrow-right" />
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SelectColegio;
